import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Check, AlertTriangle } from "lucide-react";
import { Avatar } from "@/shared/ui/Avatar";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { useToast } from "@/shared/ui/Toast";
import { Drawer } from "@/shared/ui/Drawer";
import { formatDate } from "@/shared/lib/time";
import { useCustomerDetail, useUpdateCustomer } from "../hooks";
import { ConflictError } from "../api";
import type { CustomerStatus, CustomerUpdate } from "../types";
import { IdentityForm } from "./IdentityForm";
import { AccountPremiumBlock } from "./AccountPremiumBlock";
import { TimelineSection } from "./TimelineSection";
import { DeveloperToggleBlock, AccountOperations } from "./OperationsSection";

interface CustomerDrawerProps {
  // null = closed; a number = open + load that customer.
  customerId: number | null;
  onClose: () => void;
}

// Editable string fields (status handled separately as a select of CustomerStatus).
type StrField =
  | "first_name"
  | "last_name"
  | "phone_number"
  | "birth_date"
  | "passport_number"
  | "pin"
  | "address";

const STR_FIELDS: StrField[] = [
  "first_name",
  "last_name",
  "phone_number",
  "birth_date",
  "passport_number",
  "pin",
  "address",
];

// Detail tabs (0068): identity edit / account / history. Each sensitive operation
// now lives beside the data it mutates — the developer toggle under Identity, balance
// + premium under Account — so there is no separate "Operations" tab to discover.
type DrawerTab = "identity" | "account" | "timeline";
const TABS: { key: DrawerTab; labelKey: string }[] = [
  { key: "identity", labelKey: "drawer.tabIdentity" },
  { key: "account", labelKey: "drawer.tabAccount" },
  { key: "timeline", labelKey: "drawer.tabTimeline" },
];

export function CustomerDrawer({ customerId, onClose }: CustomerDrawerProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { data, isLoading, isError } = useCustomerDetail(customerId);
  const update = useUpdateCustomer();

  // Local form state + the baseline we diff against to send only changed fields.
  const [form, setForm] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<CustomerStatus>("active");
  const [saveError, setSaveError] = useState<string | null>(null);
  // Unsaved-changes guard (0067.02): true → show the inline discard/keep confirm bar.
  const [confirmClose, setConfirmClose] = useState(false);
  // Active detail tab (0068). Defaults to identity and resets per customer.
  const [tab, setTab] = useState<DrawerTab>("identity");
  // Identity opens locked (read-only, passport/PINFL masked) until the operator presses Edit (0153).
  const [editing, setEditing] = useState(false);

  // Copy the loaded customer into the form (on load, and on Cancel of an edit).
  function resetForm() {
    if (!data) return;
    const next: Record<string, string> = {};
    for (const f of STR_FIELDS) next[f] = (data[f] ?? "") as string;
    setForm(next);
    setStatus(data.status);
    setSaveError(null);
  }

  // Reset form whenever a new customer loads.
  useEffect(() => {
    resetForm();
    setConfirmClose(false);
    setTab("identity");
    setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetForm only reads `data`
  }, [data]);

  function cancelEdit() {
    resetForm();
    setConfirmClose(false);
    setEditing(false);
  }

  const open = customerId !== null;

  const changed = useMemo(() => {
    if (!data) return new Set<string>();
    const set = new Set<string>();
    for (const f of STR_FIELDS) {
      if ((form[f] ?? "") !== ((data[f] ?? "") as string)) set.add(f);
    }
    if (status !== data.status) set.add("status");
    return set;
  }, [form, status, data]);

  // "Needs verification" if any identity field is missing.
  const needsVerify = useMemo(() => {
    if (!data) return false;
    return !data.pin || !data.passport_number || !data.birth_date || !data.address;
  }, [data]);

  function setField(f: string, v: string) {
    setForm((prev) => ({ ...prev, [f]: v }));
    setSaveError(null);
  }

  // Dirty-guard: if there are unsaved edits, surface the confirm bar instead of
  // closing immediately. Routed through Drawer.onClose, so Escape + scrim are guarded too.
  function requestClose() {
    if (changed.size > 0) {
      setConfirmClose(true);
    } else {
      onClose();
    }
  }

  async function onSave() {
    if (!data || changed.size === 0) {
      setEditing(false);
      return;
    }
    const patch: CustomerUpdate = {};
    for (const f of STR_FIELDS) {
      if (changed.has(f)) (patch as Record<string, string>)[f] = form[f];
    }
    if (changed.has("status")) patch.status = status;

    try {
      await update.mutateAsync({ id: data.id, patch });
      // Stay on the customer: the refreshed detail re-seeds the form and re-locks the view.
      setEditing(false);
      showToast(t("drawer.saved"));
    } catch (e) {
      // A save only sends identity edits — surface the failure on that tab.
      setTab("identity");
      setSaveError(e instanceof ConflictError ? t("drawer.conflict") : t("drawer.saveError"));
    }
  }

  const fullName = data ? `${data.first_name} ${data.last_name}`.trim() : "";

  return (
    <Drawer open={open} onClose={requestClose} title={t("drawer.sectionPersonal")}>
      {open && (
          <>
            <div className="flex items-center gap-3.5 border-b border-grey-200 px-4 py-4 sm:px-6 sm:py-5">
              {isLoading || !data ? (
                <div className="h-10 w-10 flex-none animate-pulse rounded-pill bg-grey-100" />
              ) : (
                <Avatar name={fullName} size="md" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <b className="min-w-0 truncate text-[17px] font-bold tracking-[-0.2px] text-grey-900">
                    {data ? fullName : t("app.loading")}
                  </b>
                  {data && <StatusBadge status={data.status} />}
                </div>
                <div className="mt-0.5 text-[12.5px] text-grey-500">
                  {data ? `NS-${String(data.id).padStart(5, "0")}` : ""}
                  {data && (
                    <span className="ml-2 text-grey-400">
                      · {t("drawer.registeredOn", { date: formatDate(data.date_joined) })}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={requestClose}
                aria-label={t("drawer.cancel")}
                className="ml-auto flex h-10 w-10 flex-none items-center justify-center rounded-control text-grey-500 transition-transform hover:bg-grey-50 hover:text-grey-900 active:scale-[0.96]"
              >
                <X size={20} strokeWidth={1.8} />
              </button>
            </div>

            {/* Tab bar (0068) — only once the customer is loaded. The identity tab carries
                an unsaved-changes dot so pending edits stay visible from any tab. */}
            {data && (
              <div className="flex gap-1 border-b border-grey-200 px-1 sm:px-6" role="tablist">
                {TABS.map((tb) => {
                  const active = tab === tb.key;
                  return (
                    <button
                      key={tb.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setTab(tb.key)}
                      className={`-mb-px flex h-11 items-center gap-1.5 border-b-2 px-3 text-[13.5px] font-semibold transition-colors ${
                        active
                          ? "border-green text-green-deep"
                          : "border-transparent text-grey-500 hover:text-grey-800"
                      }`}
                    >
                      {t(tb.labelKey)}
                      {tb.key === "identity" && changed.size > 0 && (
                        <span
                          className="h-1.5 w-1.5 rounded-full bg-warning"
                          title={t("drawer.unsavedBadge")}
                          aria-label={t("drawer.unsavedBadge")}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6 sm:py-6">
              {isError && (
                <p className="text-[13px] font-medium text-red">{t("drawer.loadError")}</p>
              )}

              {data && (
                <>
                  {tab === "identity" && (
                    <>
                      {needsVerify && (
                        <div className="mb-5 flex items-center gap-3 rounded-xl border border-warning-border bg-warning-bg px-3.5 py-3">
                          <AlertTriangle size={18} className="flex-none text-warning" />
                          <p className="text-[13px] font-medium text-warning">
                            {t("drawer.verifyBanner")}
                          </p>
                        </div>
                      )}

                      <IdentityForm
                        form={form}
                        changed={changed}
                        setField={setField}
                        status={status}
                        setStatus={(s) => {
                          setStatus(s);
                          setSaveError(null);
                        }}
                        onClearError={() => setSaveError(null)}
                        editing={editing}
                        onEdit={() => setEditing(true)}
                      />

                      {saveError && (
                        <p className="text-[13px] font-medium text-red">{saveError}</p>
                      )}

                      {/* Developer (backdoor OTP) toggle — sensitive access flag, beside identity (0068). */}
                      <DeveloperToggleBlock customer={data} />
                    </>
                  )}

                  {tab === "account" && (
                    <>
                      <AccountPremiumBlock data={data} />
                      {/* Balance adjust + premium conversion — beside the account data they change (0068). */}
                      <AccountOperations customer={data} />
                    </>
                  )}

                  {/* Lifecycle + support-action timeline (read-only, task 0066) */}
                  {tab === "timeline" && <TimelineSection customerId={data.id} />}
                </>
              )}
            </div>

            {/* Unsaved-changes confirm bar (0067.02) — mirrors the inline confirm
                pattern used by the operations section (warning destructive + grey keep). */}
            {confirmClose && (
              <div className="flex flex-col gap-2 border-t border-grey-200 bg-warning-bg px-4 py-4 sm:px-6">
                <p className="text-[13px] font-medium text-grey-800">{t("drawer.unsavedTitle")}</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmClose(false);
                      onClose();
                    }}
                    className="flex h-9 items-center rounded-control bg-warning px-3.5 text-[13px] font-semibold text-white hover:opacity-90"
                  >
                    {t("drawer.unsavedDiscard")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmClose(false)}
                    className="flex h-9 items-center rounded-control border border-grey-300 bg-white px-3.5 text-[13px] font-semibold text-grey-700 hover:bg-grey-50"
                  >
                    {t("drawer.unsavedKeep")}
                  </button>
                </div>
              </div>
            )}

            {/* Save / cancel only exist while editing — the locked view has nothing to save. */}
            {editing && (
            <div className="flex items-center gap-2.5 border-t border-grey-200 bg-grey-25 px-4 py-4 sm:px-6">
              {changed.size > 0 && (
                <span className="min-w-0 text-[12.5px] font-medium leading-snug text-warning">
                  {t("drawer.changedCount", { count: changed.size })}
                </span>
              )}
              <div className="flex-1" />
              <button
                type="button"
                onClick={cancelEdit}
                className="flex h-10 flex-none items-center whitespace-nowrap rounded-control border border-grey-300 bg-white px-4 text-[14px] font-semibold text-grey-700 transition-transform hover:bg-grey-50 active:scale-[0.98]"
              >
                {t("drawer.cancel")}
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={update.isPending || !data}
                className="flex h-10 flex-none items-center gap-2 whitespace-nowrap rounded-control bg-green px-4 text-[14px] font-semibold text-white transition-transform hover:bg-green-dark active:scale-[0.98] disabled:opacity-60"
              >
                <Check size={18} strokeWidth={2} />
                {update.isPending ? t("drawer.saving") : t("drawer.save")}
              </button>
            </div>
            )}
          </>
      )}
    </Drawer>
  );
}
